import { useEffect, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { paths } from "src/routes/paths"
import { CONFIG } from "src/config-global"
import { SplashScreen } from "src/common/loading-screen"
import { useAuthStore } from "@/src/store"
import { ROLES } from "@/src/features/internalUsers/constants"

// ----------------------------------------------------------------------

const INTERNAL_USER_ROOT = "/internal-user"

/** Query param value for login page to show "You do not have permission" */
export const LOGIN_ERROR_NO_SUPER_ADMIN = "no_super_admin_permission"

type Props = {
    children: React.ReactNode
}

function getSignInPath() {
    const { method } = CONFIG.auth
    return {
        jwt: paths.auth.jwt.signIn,
        auth0: paths.auth.auth0.signIn,
        amplify: paths.auth.amplify.signIn,
        firebase: paths.auth.firebase.signIn,
        supabase: paths.auth.supabase.signIn,
    }[method]
}

/** Check if user has INTERNAL (sales / platform) role in any workspace. Case-insensitive. */
function hasInternalInWorkspaces(user: { workspaces?: Array<{ roleName?: string }> } | null): boolean {
    return Boolean(user?.workspaces?.some((w) => String(w.roleName ?? "").toUpperCase() === ROLES.INTERNAL))
}

/**
 * Protects internal-user routes. When user lands on /internal-user:
 * - Session check (in auth slice) calls ADMIN_ME; access is granted only if data.internalWorkspaces has at least one item.
 * - If no access (empty internalWorkspaces or not logged in), redirect to login with error param.
 */
export function InternalUserGuard({ children }: Props) {
    const location = useLocation()
    const navigate = useNavigate()
    const user = useAuthStore((state) => state.user)
    const loading = useAuthStore((state) => state.loading)
    const internalUserAccessDenied = useAuthStore((state) => state.internalUserAccessDenied)
    const checkUserSession = useAuthStore((state) => state.checkUserSession)
    const logout = useAuthStore((state) => state.logout)
    const hasRedirected = useRef(false)
    const prevPathRef = useRef(location.pathname)

    const isInternalUserPath = location.pathname.startsWith(INTERNAL_USER_ROOT)
    const hasInternalAccess = hasInternalInWorkspaces(user)
    const shouldRedirectNoAccess = isInternalUserPath && user !== null && !hasInternalAccess
    const shouldRedirectNotLoggedIn = isInternalUserPath && user === null && !loading

    // When user navigates to /internal-user, re-run session check so ADMIN_ME is called (and access gated by internalWorkspaces)
    useEffect(() => {
        const justEnteredInternalUser = isInternalUserPath && !prevPathRef.current.startsWith(INTERNAL_USER_ROOT)
        prevPathRef.current = location.pathname
        if (justEnteredInternalUser && user !== null) {
            checkUserSession?.(true)
        }
    }, [location.pathname, isInternalUserPath, user, checkUserSession])

    useEffect(() => {
        if (!isInternalUserPath || hasRedirected.current) return
        const signInPath = getSignInPath()
        if (shouldRedirectNoAccess) {
            hasRedirected.current = true
            logout()
            navigate(`${signInPath}?error=${LOGIN_ERROR_NO_SUPER_ADMIN}`, { replace: true })
            return
        }
        if (shouldRedirectNotLoggedIn) {
            hasRedirected.current = true
            navigate(`${signInPath}${internalUserAccessDenied ? `?error=${LOGIN_ERROR_NO_SUPER_ADMIN}` : ""}`, {
                replace: true,
            })
        }
    }, [
        isInternalUserPath,
        shouldRedirectNoAccess,
        shouldRedirectNotLoggedIn,
        internalUserAccessDenied,
        logout,
        navigate,
    ])

    if (isInternalUserPath && user === null) {
        return <SplashScreen />
    }

    if (shouldRedirectNoAccess) {
        return <SplashScreen />
    }

    return <>{children}</>
}
